import moment from 'moment';

import type { GeneralSettings } from 'types/scheduler';

export type ShiftLike = {
	id?: number | string;
	startAt?: string | Date;
	endAt?: string | Date;
	start?: string | Date;
	end?: string | Date;
	employee?: { id?: number | string; ['@id']?: string } | null;
};

export type EmployeeStatusData = {
	hasOvertime: boolean;
	isApproachingDailyOvertime: boolean;
	isApproachingWeeklyOvertime: boolean;
	overtimeHours: number;
	weeklyOvertimeHours: number;
	todayOvertimeHours: number;
	todayHours: number;
	hasDailyOvertime: boolean;
	hasWeeklyOvertime: boolean;
	maxDailyHours: number;
	totalHours: number;
	isMinor: boolean;
	hasTimeOff: boolean;
	hasAvailabilityIssue: boolean;
	availabilityMessage: string | null;
	isShiftCausingDailyOT: boolean;
	isShiftCausingDailyApproach: boolean;
	isShiftCausingWeeklyOT: boolean;
	isShiftCausingWeeklyApproach: boolean;
	dailyOTWarning: number;
	weeklyOTWarning: number;
};

export type OvertimeThresholds = {
	dailyOTThreshold: number;
	dailyOTWarning: number;
	weeklyOTThreshold: number;
	weeklyOTWarning: number;
	minorAge: number;
};

type DayLike = {
	position?: string | number;
	label?: string;
	['@id']?: string;
};

type WorkplaceUserLike = {
	dateOfBirth?: string | null;
	userTimeOff?: Array<{ startAt?: string; endAt?: string }>;
	userAvailability?: Array<{
		day?: string | { ['@id']?: string };
		startAt?: string;
		endAt?: string;
	}>;
};

const getShiftStart = (shift: ShiftLike) => moment(shift.startAt || shift.start);
const getShiftEnd = (shift: ShiftLike) => moment(shift.endAt || shift.end);
const getShiftHours = (shift: ShiftLike) =>
	moment.duration(getShiftEnd(shift).diff(getShiftStart(shift))).asHours();

export const getOvertimeThresholds = (
	generalSettings?: Partial<GeneralSettings> | null
): OvertimeThresholds => {
	const dailyOTThreshold = generalSettings?.overtimeDailyHoursAt ?? 8;
	const dailyOTWarning =
		generalSettings?.overtimeDailyWarningAt ?? dailyOTThreshold * 0.9;
	const weeklyOTThreshold = generalSettings?.overtimeWeeklyHoursAt ?? 40;
	const weeklyOTWarning = generalSettings?.overtimeWeeklyWarningAt ?? weeklyOTThreshold;

	return {
		dailyOTThreshold,
		dailyOTWarning,
		weeklyOTThreshold,
		weeklyOTWarning,
		minorAge: generalSettings?.ageOfMinor ?? 18,
	};
};

export const calculateEmployeeStatusForShift = (args: {
	employeeId: number | string;
	currentShift: ShiftLike;
	allShifts: ShiftLike[];
	fromDate: string | Date;
	toDate: string | Date;
	generalSettings?: Partial<GeneralSettings> | null;
	workplaceUser?: WorkplaceUserLike | null;
	allDays?: DayLike[] | null;
}): EmployeeStatusData => {
	const {
		employeeId,
		currentShift,
		allShifts,
		fromDate,
		toDate,
		generalSettings,
		workplaceUser,
		allDays,
	} = args;

	const {
		dailyOTThreshold,
		dailyOTWarning,
		weeklyOTThreshold,
		weeklyOTWarning,
		minorAge,
	} = getOvertimeThresholds(generalSettings);

	const weekStart = moment(fromDate).startOf('day');
	const weekEnd = moment(toDate).endOf('day');
	const employeeIdStr = String(employeeId);

	const employeeShifts = allShifts.filter((shift) => {
		if (String(shift.employee?.id) !== employeeIdStr) return false;
		return getShiftStart(shift).isBetween(weekStart, weekEnd, null, '[]');
	});

	let totalHours = 0;
	employeeShifts.forEach((shift) => {
		totalHours += getShiftHours(shift);
	});

	const hasWeeklyOT = totalHours > weeklyOTThreshold;
	const isApproachingWeeklyOT = !hasWeeklyOT && totalHours >= weeklyOTWarning;

	let hasDailyOT = false;
	let isApproachingDailyOT = false;
	let maxDailyHours = 0;
	let todayHours = 0;
	let todayOvertimeHours = 0;
	let isShiftCausingDailyOT = false;
	let isShiftCausingDailyApproach = false;

	const shiftsByDayArray = new Map<string, ShiftLike[]>();
	employeeShifts.forEach((shift) => {
		const dayKey = getShiftStart(shift).format('YYYY-MM-DD');
		if (!shiftsByDayArray.has(dayKey)) {
			shiftsByDayArray.set(dayKey, []);
		}
		shiftsByDayArray.get(dayKey)!.push(shift);
	});

	const currentShiftDayKey = getShiftStart(currentShift).format('YYYY-MM-DD');

	shiftsByDayArray.forEach((dayShifts, dayKey) => {
		dayShifts.sort((a, b) => getShiftStart(a).valueOf() - getShiftStart(b).valueOf());

		let dailyAccumulator = 0;
		let dayHasDailyOT = false;
		let dayHasDailyApproach = false;

		for (const shift of dayShifts) {
			dailyAccumulator += getShiftHours(shift);

			if (String(shift.id) === String(currentShift.id)) {
				if (dailyAccumulator > dailyOTThreshold) {
					isShiftCausingDailyOT = true;
				} else if (dailyAccumulator >= dailyOTWarning) {
					isShiftCausingDailyApproach = true;
				}
			}

			if (dailyAccumulator > dailyOTThreshold) {
				dayHasDailyOT = true;
			} else if (dailyAccumulator >= dailyOTWarning) {
				dayHasDailyApproach = true;
			}
		}

		if (dayKey === currentShiftDayKey) {
			todayHours = dailyAccumulator;
			if (dailyAccumulator > dailyOTThreshold) {
				todayOvertimeHours = dailyAccumulator - dailyOTThreshold;
			}
		}

		if (dailyAccumulator > maxDailyHours) {
			maxDailyHours = dailyAccumulator;
		}

		if (dayHasDailyOT) {
			hasDailyOT = true;
		} else if (dayHasDailyApproach) {
			isApproachingDailyOT = true;
		}
	});

	let isShiftCausingWeeklyOT = false;
	let isShiftCausingWeeklyApproach = false;
	const sortedWeekShifts = [...employeeShifts].sort(
		(a, b) => getShiftStart(a).valueOf() - getShiftStart(b).valueOf()
	);

	if (hasWeeklyOT) {
		let weeklyAccumulator = 0;
		for (const shift of sortedWeekShifts) {
			weeklyAccumulator += getShiftHours(shift);
			if (
				String(shift.id) === String(currentShift.id) &&
				weeklyAccumulator > weeklyOTThreshold
			) {
				isShiftCausingWeeklyOT = true;
			}
		}
	} else if (isApproachingWeeklyOT) {
		let weeklyAccumulator = 0;
		for (const shift of sortedWeekShifts) {
			weeklyAccumulator += getShiftHours(shift);
			if (
				String(shift.id) === String(currentShift.id) &&
				weeklyAccumulator >= weeklyOTWarning
			) {
				isShiftCausingWeeklyApproach = true;
			}
		}
	}

	const hasOT = hasDailyOT || hasWeeklyOT;
	const otHours = hasDailyOT
		? maxDailyHours - dailyOTThreshold
		: hasWeeklyOT
			? totalHours - weeklyOTThreshold
			: 0;

	const employeeDOB = workplaceUser?.dateOfBirth;
	const isMinor = employeeDOB
		? moment().diff(moment(employeeDOB), 'years') < minorAge
		: false;

	const hasTimeOff = (workplaceUser?.userTimeOff || []).some((timeOff) => {
		if (!timeOff.startAt || !timeOff.endAt) return false;
		const timeOffStart = moment(`${timeOff.startAt}.000Z`);
		const timeOffEnd = moment(`${timeOff.endAt}.000Z`);
		return (
			timeOffStart.isBetween(weekStart, weekEnd, 'day', '[]') ||
			timeOffEnd.isBetween(weekStart, weekEnd, 'day', '[]') ||
			(timeOffStart.isSameOrBefore(weekStart) && timeOffEnd.isSameOrAfter(weekEnd))
		);
	});

	let hasAvailabilityIssue = false;
	let availabilityMessage: string | null = null;

	if (workplaceUser && allDays?.length) {
		const shiftStart = getShiftStart(currentShift);
		const shiftEnd = getShiftEnd(currentShift);
		const shiftDayOfWeek = shiftStart.day();
		const shiftStartTimeOnly = shiftStart.format('HH:mm:ss');
		const shiftEndTimeOnly = shiftEnd.format('HH:mm:ss');
		const userAvailability = workplaceUser.userAvailability || [];
		const hasAnyAvailabilityInWeek = userAvailability.length > 0;

		const shiftDayObj = allDays.find(
			(day) => parseInt(String(day.position), 10) === shiftDayOfWeek
		);

		if (shiftDayObj) {
			const shiftDayName = shiftDayObj.label || '';
			const shiftDayId = shiftDayObj['@id'];

			const matchingAvails = userAvailability.filter((avail) => {
				if (!avail.day) return false;
				const availDayId = typeof avail.day === 'string' ? avail.day : avail.day['@id'];
				return availDayId === shiftDayId;
			});

			let latestAvailability = null as (typeof matchingAvails)[number] | null;
			if (matchingAvails.length > 0) {
				latestAvailability = matchingAvails.reduce((latest, current) => {
					if (!latest) return current;
					return moment(current.startAt).isAfter(moment(latest.startAt))
						? current
						: latest;
				}, matchingAvails[0]);
			}

			if (latestAvailability?.startAt && latestAvailability?.endAt) {
				const availStartTimeOnly = moment(latestAvailability.startAt).format('HH:mm:ss');
				const availEndTimeOnly = moment(latestAvailability.endAt).format('HH:mm:ss');

				if (availStartTimeOnly === availEndTimeOnly) {
					hasAvailabilityIssue = true;
					availabilityMessage = `Not available on ${shiftDayName}`;
				} else {
					const isOvernightAvailability = availEndTimeOnly < availStartTimeOnly;
					const isWithin = isOvernightAvailability
						? (shiftStartTimeOnly >= availStartTimeOnly ||
								shiftStartTimeOnly <= availEndTimeOnly) &&
							(shiftEndTimeOnly >= availStartTimeOnly ||
								shiftEndTimeOnly <= availEndTimeOnly)
						: shiftStartTimeOnly >= availStartTimeOnly &&
							shiftEndTimeOnly <= availEndTimeOnly;

					if (!isWithin) {
						hasAvailabilityIssue = true;
						availabilityMessage = `Available on ${shiftDayName}: ${moment(
							latestAvailability.startAt
						).format('h:mm A')} - ${moment(latestAvailability.endAt).format('h:mm A')}`;
					}
				}
			} else if (hasAnyAvailabilityInWeek) {
				hasAvailabilityIssue = true;
				availabilityMessage = `Not available on ${shiftDayName}`;
			}
		}
	}

	return {
		hasOvertime: hasOT,
		isApproachingDailyOvertime:
			isApproachingDailyOT && !hasDailyOT && isShiftCausingDailyApproach,
		isApproachingWeeklyOvertime: isApproachingWeeklyOT && !hasOT,
		overtimeHours: otHours,
		weeklyOvertimeHours: hasWeeklyOT ? totalHours - weeklyOTThreshold : 0,
		todayOvertimeHours,
		todayHours,
		hasDailyOvertime: hasDailyOT,
		hasWeeklyOvertime: hasWeeklyOT,
		maxDailyHours,
		totalHours,
		isMinor,
		hasTimeOff,
		hasAvailabilityIssue,
		availabilityMessage,
		isShiftCausingDailyOT,
		isShiftCausingDailyApproach,
		isShiftCausingWeeklyOT,
		isShiftCausingWeeklyApproach,
		dailyOTWarning,
		weeklyOTWarning,
	};
};

export const enrichShiftsWithEmployeeStatus = <T extends ShiftLike>(args: {
	shifts: T[];
	fromDate: string | Date;
	toDate: string | Date;
	generalSettings?: Partial<GeneralSettings> | null;
	workplaceUsersMap?: Map<string, WorkplaceUserLike>;
	allDays?: DayLike[] | null;
}): Array<T & { employeeStatusData: EmployeeStatusData | null }> => {
	const {
		shifts,
		fromDate,
		toDate,
		generalSettings,
		workplaceUsersMap,
		allDays,
	} = args;

	return shifts.map((shift) => {
		if (!shift.employee?.id) {
			return { ...shift, employeeStatusData: null };
		}

		const workplaceUser =
			workplaceUsersMap?.get(shift.employee['@id'] || `/api/users/${shift.employee.id}`) ||
			null;

		return {
			...shift,
			employeeStatusData: calculateEmployeeStatusForShift({
				employeeId: shift.employee.id,
				currentShift: shift,
				allShifts: shifts,
				fromDate,
				toDate,
				generalSettings,
				workplaceUser,
				allDays,
			}),
		};
	});
};
